/**
 * @author        matthew@zumper.com (Matthew Talamini)
 * @copyright     Copyright (c) 2024, Zumper
 * @description   BuildingReviewsData bizo type
 */

import type { Bizo } from './base'

export interface BuildingReviewsDataMetaType {
  readonly currentKey: number | null
  readonly error: unknown
  readonly fetching: boolean
  readonly fetchingKey: number | null
}

export interface RawBuildingReviewsData {
  readonly sources?: RawBuildingReviewsSources
}

export declare class BuildingReviewsData extends Bizo<BuildingReviewsData, RawBuildingReviewsData> {
  get sources(): BuildingReviewsSources
}

export enum BuildingReviewsSourceNames {
  FACEBOOK = 'Facebook',
  GOOGLE = 'Google',
  YELP = 'Yelp',
}

export declare class BuildingReviewsSources extends Bizo<
  BuildingReviewsSources,
  RawBuildingReviewsSources
> {
  get [BuildingReviewsSourceNames.GOOGLE](): BuildingReviewsSource
}

export interface RawBuildingReviewsSources {
  [BuildingReviewsSourceNames.GOOGLE]: RawBuildingReviewsSource
}

export declare class BuildingReviewsSource extends Bizo<
  BuildingReviewsSource,
  RawBuildingReviewsSource
> {
  get aggregateRating(): number
  get ratingScale(): number
  get reviewCount(): number
  get externalUrl(): string
  get lastPulled(): number | null
  get reviews(): readonly BuildingReviewsReview[]
}

export interface RawBuildingReviewsSource {
  readonly aggregate_rating: number
  readonly rating_scale: number
  readonly review_count: number
  readonly external_url: string
  readonly last_pulled: number | null
  readonly reviews: readonly RawBuildingReviewsReview[]
}

export declare class BuildingReviewsReview extends Bizo<
  BuildingReviewsReview,
  RawBuildingReviewsReview
> {
  get name(): string
  get avatarUrl(): string
  get date(): number | null
  get rating(): number
  get body(): string
  get response(): BuildingReviewsResponse | undefined
}

export interface RawBuildingReviewsReview {
  readonly name: string
  readonly avatar_url: string
  readonly date: number | null
  readonly rating: number
  readonly body: string
  readonly response?: RawBuildingReviewsResponse
}

export declare class BuildingReviewsResponse extends Bizo<
  BuildingReviewsResponse,
  RawBuildingReviewsResponse
> {
  get date(): number | null
  get body(): string
}

export interface RawBuildingReviewsResponse {
  readonly date: number | null
  readonly body: string
}

export interface BuildingReviewsDataModule {
  readonly data: BuildingReviewsData
  readonly meta: BuildingReviewsDataMetaType
}
